import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ConfigProvider, Steps, theme } from "antd";
import { observer } from "mobx-react";
import { useEffect, useRef } from "react";
import { useStores } from "../../../../store/stores.js";
import { __ } from "../../../../utils/i18n.js";
/**
 * Progress stepper — desktop renders Antd `Steps` in a horizontal rail,
 * mobile replaces it with a compact segment bar (current label + "Step X
 * of N" counter + filled/empty segments). The two share JSX; CSS hides
 * whichever isn't appropriate for the breakpoint.
 */ const ProgressIndicator = observer(()=>{
    const { checkoutV2Store } = useStores();
    const { currentStep, product } = checkoutV2Store;
    const { token } = theme.useToken();
    const isOrderConfirmProduct = !!product && [
        "6",
        "7"
    ].includes(product.data.productPlan.product.id);
    const lastStepLabel = isOrderConfirmProduct ? __("Order confirmed") : __("License & Download");
    const stepLabels = [
        __("Plan selected"),
        __("Your details"),
        __("Payment"),
        lastStepLabel
    ];
    const totalSteps = stepLabels.length;
    const statusFor = (index)=>{
        if (index < currentStep) {
            return "finish";
        }
        if (index === currentStep) {
            // Step 1 is always finished (plan selected).
            if (index === 0) {
                return "finish";
            }
            return "process";
        }
        return "wait";
    };
    const containerRef = useRef(null);
    useEffect(()=>{
        const container = containerRef.current;
        if (!container) {
            return;
        }
        const items = container.querySelectorAll(`.${process.env.ANTD_PREFIX}-steps-item`);
        const active = items[currentStep];
        if (active && "scrollIntoView" in active) {
            active.scrollIntoView({
                behavior: "smooth",
                inline: "center",
                block: "nearest"
            });
        }
    }, [
        currentStep
    ]);
    const progressLabel = __("Step %d of %d: %s", currentStep + 1, totalSteps, stepLabels[currentStep]);
    return /*#__PURE__*/ _jsxs(ConfigProvider, {
        theme: {
            token: {
                colorPrimary: "#45b3dd"
            }
        },
        children: [
            /*#__PURE__*/ _jsx("div", {
                ref: containerRef,
                className: "checkout-v2-stepper-scroll",
                role: "progressbar",
                "aria-valuenow": currentStep + 1,
                "aria-valuemin": 1,
                "aria-valuemax": totalSteps,
                "aria-valuetext": progressLabel,
                children: /*#__PURE__*/ _jsx(Steps, {
                    responsive: false,
                    size: "small",
                    current: currentStep,
                    items: stepLabels.map((title, index)=>({
                            title,
                            status: statusFor(index)
                        }))
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "checkout-v2-stepper-segments",
                role: "progressbar",
                "aria-valuenow": currentStep + 1,
                "aria-valuemin": 1,
                "aria-valuemax": totalSteps,
                "aria-valuetext": progressLabel,
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "checkout-v2-stepper-segments__header",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "checkout-v2-stepper-segments__label",
                                style: {
                                    color: token.colorTextHeading
                                },
                                children: stepLabels[currentStep]
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "checkout-v2-stepper-segments__counter",
                                style: {
                                    color: token.colorTextSecondary
                                },
                                children: __("Step %d of %d", currentStep + 1, totalSteps)
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "checkout-v2-stepper-segments__bar",
                        "aria-hidden": true,
                        children: stepLabels.map((_, index)=>/*#__PURE__*/ _jsx("span", {
                                className: `checkout-v2-stepper-segments__segment${index <= currentStep ? " checkout-v2-stepper-segments__segment--filled" : ""}`,
                                style: {
                                    background: index <= currentStep ? token.colorPrimary : token.colorFillTertiary
                                }
                            }, index))
                    })
                ]
            })
        ]
    });
});
export { ProgressIndicator };
